/**
 * Feature definitions for the toggle system.
 * Pure constants — no React dependencies.
 *
 * Two-layer design:
 *   Layer 1 (tier):  subscription_tier on user_settings (free/pro/enterprise)
 *                    — tier gates feature availability based on subscription plan
 *   Layer 2 (prefs): per-feature boolean toggles the user can flip in Settings → Features
 *
 * A feature is enabled only if BOTH layers allow it:
 *   - Tier allows: TIER_HIERARCHY[userTier] >= TIER_HIERARCHY[feature.tier]
 *   - User hasn't disabled it in Settings → Features
 */

import { tierHasAccess } from './tierLimits'

export const FEATURES = [
  {
    key: "gantt",
    dbColumn: "feature_gantt",
    label: "Gantt Chart",
    description: "Visual schedule with critical path and dependencies.",
    tier: "pro",
  },
  {
    key: "changeOrders",
    dbColumn: "feature_change_orders",
    label: "Change Orders",
    description: "Track scope changes with approval workflow.",
    tier: "pro",
  },
  {
    key: "budget",
    dbColumn: "feature_budget",
    label: "Budget & Cost Tracking",
    description: "Task cost line items, budget summaries, and budget reports.",
    tier: "pro",
  },
  {
    key: "dailyLogs",
    dbColumn: "feature_daily_logs",
    label: "Daily Logs",
    description: "Record daily site activity, weather, and crew notes.",
    tier: "pro",
  },
  {
    key: "reports",
    dbColumn: "feature_reports",
    label: "Reports",
    description: "Portfolio, Vendor Insurance, and Overdue/Blocked reports.",
    tier: "free",
  },
  {
    key: "contacts",
    dbColumn: "feature_contacts",
    label: "Contact Directory",
    description: "Manage vendors, employees, clients, and stakeholders.",
    tier: "free",
  },
  {
    key: "templates",
    dbColumn: "feature_templates",
    label: "Templates",
    description: "Task template library and builder for reusable project plans.",
    tier: "free",
  },
  {
    key: "nlQuery",
    dbColumn: "feature_nl_query",
    label: "Ask Baulit (AI)",
    description: "Ask questions about your projects in plain English. Requires an Anthropic API key.",
    tier: "pro",
  },
  {
    key: "punchList",
    dbColumn: "feature_punch_list",
    label: "Punch List",
    description: "Track closeout items from final walkthrough.",
    tier: "pro",
  },
  {
    key: "calendar",
    dbColumn: "feature_calendar",
    label: "Calendar View",
    description: "Month/week calendar showing tasks by due date and daily log indicators.",
    tier: "pro",
  },
  {
    key: "nlqV2",
    dbColumn: "feature_nlq_v2",
    label: "Ask Baulit V2 (Patent)",
    description: "Registry-backed query orchestration with validation gate. Experimental.",
    tier: "pro",
  },
];

/** Free-tier defaults — used as fallback when userSettings is null (not loaded yet).
 *  Fail-closed: only free-tier features are enabled until settings load successfully. */
export const DEFAULT_ENABLED_FEATURES = Object.fromEntries(
  FEATURES.map(f => [f.key, tierHasAccess("free", f.tier)])
);

/**
 * Given a userSettings object (camelCase keys matching FEATURES[].key),
 * returns an enabledFeatures map: { gantt: bool, changeOrders: bool, ... }
 *
 * When userSettings is null (not loaded / failed to load), returns free-tier defaults
 * (fail-closed — paid features require successful settings load).
 *
 * Two-layer check:
 *   1. Tier gate: user's subscription tier must be >= feature's required tier
 *   2. User pref: user hasn't toggled it off in Settings → Features
 *
 * Features locked by tier cannot be enabled by user preference.
 */
export function computeEnabledFeatures(userSettings) {
  if (!userSettings) return { ...DEFAULT_ENABLED_FEATURES };
  const tier = userSettings.subscriptionTier || "free";
  const result = {};
  for (const f of FEATURES) {
    const tierAllowed = tierHasAccess(tier, f.tier);
    const userEnabled = userSettings[f.key] !== false;
    result[f.key] = tierAllowed && userEnabled;
  }
  return result;
}